import { localizeOrgRoleEnumText } from '@/lib/org-role-labels';

export const NotificationType = {
  // 组织邀请
  ORG_INVITATION_RECEIVED: 10,
  ORG_INVITATION_ACCEPTED: 11,
  ORG_INVITATION_DECLINED: 12,
  // 成员变动
  ORG_MEMBER_REMOVED: 20,
  ORG_MEMBER_SUSPENDED: 21,
  ORG_ROLE_CHANGED: 22,
  ORG_MEMBER_IDENTITY_CHANGED: 23,
  ORG_MEMBER_POSITIONS_CHANGED: 24,
  ORG_MEMBER_LEFT: 26,
  // AI 任务
  AI_TASK_COMPLETED: 30,
  AI_TASK_FAILED: 31,
  // 词元
  CREDITS_LOW_BALANCE: 40,
  ORG_CREDITS_INSUFFICIENT: 41,
  CREDITS_EXHAUSTED: 42,
  // 企业认证
  CERT_APPROVED: 50,
  CERT_REJECTED: 51,
  CERT_NEED_SUPPLEMENT: 52,
  // 账号状态
  ACCOUNT_APPEAL_APPROVED: 80,
  // 项目
  PROJECT_MEMBER_ADDED: 60,
  PROJECT_SHARED: 61,
  // RFC-003 §10.3：项目级成员/权限变更。
  PROJECT_MEMBER_INVITED: 62,
  PROJECT_MEMBER_GROUPS_CHANGED: 63,
  PROJECT_CONVERTED_TO_AGGREGATE: 64,
  PROJECT_ORG_COLLAB_INVITED: 65,
  PROJECT_ORG_COLLAB_ACCEPTED: 66,
  PROJECT_ORG_COLLAB_REVOKED: 67,
  PROJECT_MEMBER_AUTH_CHANGED: 68,
  PROJECT_MEMBER_REMOVED: 69,
  PROJECT_PRODUCER_SETUP_REQUIRED: 680,
  PROJECT_DIRECTOR_SETUP_REQUIRED: 681,
  PROJECT_FUNCTIONAL_ROLE_ASSIGNED: 682,
  PROJECT_CREDIT_EXHAUSTED: 687,
  PROJECT_ORG_CREDIT_INSUFFICIENT: 688,
  // 历史兼容：旧资源设计请求通知不再生产，仅用于旧消息通用展示。
  PROJECT_RESOURCE_DESIGN_REQUESTED: 693,
  STORYBOARD_REVIEW_REQUESTED: 694,
  STORYBOARD_REVIEW_APPROVED: 695,
  STORYBOARD_REVIEW_REJECTED: 696,
  PROJECT_MEMBER_SETUP_REQUIRED: 697,
  PROJECT_EXTERNAL_MEMBER_REVIEW_REQUIRED: 698,
  PROJECT_EXTERNAL_MEMBER_APPROVED: 699,
  PROJECT_EXTERNAL_MEMBER_REJECTED: 700,
  PROJECT_EXTERNAL_MEMBER_INVITED: 711,
  PROJECT_EXTERNAL_MEMBER_PENDING_REVIEW: 712,
  PROJECT_TRANSFER_PENDING_CREDIT: 724,
  PROJECT_EPISODE_BUDGET_REQUIRED: 725,
  SCREENPLAY_PARSE_COMPLETED: 726,
  PROJECT_ORG_COLLAB_TERMINATED: 727,
  PROJECT_ORG_FUNCTIONAL_ROLE_REVOKED: 728,
  PROJECT_ORG_FUNCTIONAL_ROLE_GRANTED: 729,
  PROJECT_RESOURCE_DELETE_REQUESTED: 720,
  PROJECT_RESOURCE_DELETE_APPROVED: 721,
  PROJECT_RESOURCE_DELETE_REJECTED: 722,
  PROJECT_RESOURCE_DELETE_FAILED: 723,
  PROJECT_SUBPROJECT_CAP_EXHAUSTED: 689,
  // 临时成员
  TEMP_MEMBER_INVITED: 701,
  TEMP_MEMBER_INVITE_ACCEPTED: 702,
  TEMP_MEMBER_EXPIRES_IN_7_DAYS: 703,
  TEMP_MEMBER_EXPIRES_IN_1_DAY: 704,
  TEMP_MEMBER_PROJECT_REMOVE_REQUEST: 705,
  TEMP_MEMBER_PROJECT_REMOVE_ACCEPTED: 706,
  TEMP_MEMBER_PROJECT_REMOVE_DECLINED: 707,
  TEMP_MEMBER_REMOVED: 708,
  TEMP_MEMBER_REPLACEMENT_REMINDER: 709,
  TEMP_MEMBER_BANNED: 710,
  // 系统
  SYSTEM_ANNOUNCEMENT: 90,
} as const;

export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

export interface Notification {
  notificationId: string;
  type: NotificationType;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  isRead: boolean;
  actionRequired: boolean;
  actionTaken: boolean;
  expiresAt: string | null;
  createdAt: string;
  readAt: string | null;
}

/** 后端 camelCase 响应 → Notification（保留 snake 兼容用于旧缓存/过渡期） */
export function parseNotification(raw: Record<string, unknown>): Notification {
  return {
    notificationId: (raw.notificationId ?? raw.notification_id) as string,
    type: (raw.type ?? 0) as NotificationType,
    title: localizeOrgRoleEnumText((raw.title ?? '') as string),
    body: localizeOrgRoleEnumText((raw.body ?? '') as string),
    metadata: (raw.metadata ?? raw.extra_data ?? {}) as Record<string, unknown>,
    isRead: (raw.isRead ?? raw.is_read ?? false) as boolean,
    actionRequired: (raw.actionRequired ?? raw.action_required ?? false) as boolean,
    actionTaken: (raw.actionTaken ?? raw.action_taken ?? false) as boolean,
    expiresAt: (raw.expiresAt ?? raw.expires_at ?? null) as string | null,
    createdAt: (raw.createdAt ?? raw.created_at) as string,
    readAt: (raw.readAt ?? raw.read_at ?? null) as string | null,
  };
}
